import { CommonModule } from "@angular/common";
import { Component, inject, OnInit, ViewChild } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { ToastService } from "@dffedb/ui";
import { PushNotifications } from "@capacitor/push-notifications";
import { catchError, of, take } from "rxjs";

import { UmbracoIndstillingerObserver } from "@e-forsyning/common/umbraco-indstillinger";

import { PushNotificationService } from "./push-notification.service";
import { NotificationPermissionRequestComponent } from "./notification-permission-request.component";

@Component({
    selector: "eforsyning-notification-dialog",
    standalone: true,
    imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatSlideToggleModule, MatProgressSpinnerModule, NotificationPermissionRequestComponent],
    templateUrl: "./notification-dialog.component.html",
    styleUrls: ["./notification-dialog.component.scss"]
})
export class NotificationDialogComponent implements OnInit {
    private readonly dialogRef = inject(MatDialogRef<NotificationDialogComponent>);
    private readonly pushNotificationService = inject(PushNotificationService);
    private readonly toastService = inject(ToastService);
    private readonly umbracoIndstillingerObserver = inject(UmbracoIndstillingerObserver);

    @ViewChild(NotificationPermissionRequestComponent) notificationPermissionRequestComponent?: NotificationPermissionRequestComponent;

    public isNotificationEnabled = this.pushNotificationService.isNotificationEnabled();
    public isLoading = false;
    public isNotificationPermissionGranted = false;
    private hasUserToggled = false;

    public async ngOnInit(): Promise<void> {
        this.isNotificationPermissionGranted = await this.checkNotificationsEnabled();
        this.loadNotificationEnabledStatus();
    }

    public closeDialog(): void {
        this.dialogRef.close();
    }

    public onToggleChange(enabled: boolean): void {
        const notificationPermissionRequestComponent = this.notificationPermissionRequestComponent;
        if (!notificationPermissionRequestComponent) {
            this.toastService.visFejl("Notifikations-komponenten kunne ikke initialiseres");
            this.isNotificationEnabled = this.pushNotificationService.isNotificationEnabled();
            return;
        }

        this.hasUserToggled = true;

        if (enabled) {
            this.pushNotificationService.resetUnsubscribeStatus();
            this.isNotificationEnabled = true;
            notificationPermissionRequestComponent.requestPermission();
            return;
        }

        this.isNotificationEnabled = false;
        notificationPermissionRequestComponent.blockNotifications();
    }

    public requestPermissionsFromDevice(): void {
        PushNotifications.requestPermissions().then((result) => {
            if (result.receive === "granted") {
                this.toastService.visInfo("Notifikationstilladelse givet");
            } else if (result.receive === "denied") {
                this.toastService.visFejl("Notifikationstilladelse nægtet");
            } else {
                this.toastService.visInfo("Der skete en fejl ved anmodning om notifikationstilladelse");
            }
        });
    }

    get notificationsEnabled(): boolean {
        return this.isNotificationEnabled;
    }

    public async checkNotificationsEnabled(): Promise<boolean> {
        const result = await PushNotifications.checkPermissions();
        return result.receive === "granted";
    }

    private loadNotificationEnabledStatus(): void {
        this.isNotificationEnabled = this.pushNotificationService.isNotificationEnabled();

        this.umbracoIndstillingerObserver.valueChanges.pipe(take(1)).subscribe((indstillinger) => {
            const appServerUrl = indstillinger?.aktuelEforsyning?.appServerUrl;
            if (!appServerUrl) {
                return;
            }

            this.pushNotificationService
                .checkNotificationStatusOnAppServer(appServerUrl)
                .pipe(catchError(() => of({ enabled: this.isNotificationEnabled })))
                .subscribe((status) => {
                    if (this.hasUserToggled) {
                        return;
                    }
                    this.isNotificationEnabled = this.pushNotificationService.hasUnsubscribedFromNotifications() ? false : status.enabled;
                });
        });
    }
}
